import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, RefreshCw, AlertCircle, ExternalLink, ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getDocumentation, listDocumentation, type HelpDocument } from '@/lib/help/api';
import { getDocInfoForRoute } from '@/lib/help/routes';
import { MarkdownRenderer } from '@/components/help/MarkdownRenderer';

export const Route = createFileRoute('/_dashboard/docs/$')({
  component: DocumentationContentPage,
});

function DocumentationContentPage() {
  const params = useParams({ from: '/_dashboard/docs/$' });
  const docPath = '/' + (params['_splat'] || '');

  const [docContent, setDocContent] = useState<string | null>(null);
  const [childDocs, setChildDocs] = useState<HelpDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const docInfo = getDocInfoForRoute(docPath);

  const loadDocumentation = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to load the documentation content
      const doc = await getDocumentation(docPath);
      if (doc) {
        setDocContent(doc.content);
      } else {
        setDocContent(null);
      }

      // Also load child documents for this path
      const allDocs = await listDocumentation();
      const children = allDocs.filter((d) => {
        const normalizedDocPath = docPath.replace(/^\//, '');
        const normalizedRoute = d.route.replace(/^\//, '');
        // Check if this doc is a direct child (one level deeper)
        if (!normalizedRoute.startsWith(normalizedDocPath + '/')) return false;
        // Exclude the current path itself
        if (normalizedRoute === normalizedDocPath) return false;
        // Check if it's a direct child (not deeper nested)
        const remainder = normalizedRoute.slice(normalizedDocPath.length + 1);
        return !remainder.includes('/');
      });
      setChildDocs(children);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documentation');
      console.error('Documentation load error:', err);
    } finally {
      setLoading(false);
    }
  }, [docPath]);

  useEffect(() => {
    loadDocumentation();
  }, [loadDocumentation]);

  // Build breadcrumb from path
  const pathParts = docPath.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, index) => ({
    label: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '),
    path: '/docs/' + pathParts.slice(0, index + 1).join('/'),
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-card">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Link to="/docs">
            <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8" title="Back to docs">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0 flex-1">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
              <Link to="/docs" className="hover:text-foreground">
                Docs
              </Link>
              {breadcrumbs.map((crumb, index) => (
                <span key={crumb.path} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-foreground">{crumb.label}</span>
                  ) : (
                    <Link to={crumb.path} className="hover:text-foreground">
                      {crumb.label}
                    </Link>
                  )}
                </span>
              ))}
            </div>
            <h3 className="font-semibold text-sm truncate">{docInfo.title}</h3>
            <p className="text-xs text-muted-foreground truncate mt-0.5">{docInfo.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <Link to={docPath as '/'}>
            <Button variant="outline" size="sm" title="Go to feature page">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Feature
            </Button>
          </Link>
          <Button variant="ghost" size="icon" onClick={loadDocumentation} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6 max-w-4xl mx-auto">
          {loading ? (
            <DocumentationSkeleton />
          ) : error ? (
            <DocumentationError message={error} onRetry={loadDocumentation} />
          ) : (
            <>
              {/* Documentation content */}
              {docContent && (
                <div className="prose prose-sm dark:prose-invert max-w-none mb-8">
                  <MarkdownRenderer content={docContent} />
                </div>
              )}

              {/* Child documents */}
              {childDocs.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-lg font-semibold mb-4">
                    {docContent ? 'Related Documentation' : 'Documentation in this section'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {childDocs.map((doc) => (
                      <Link key={doc.route} to={`/docs${doc.route}` as any}>
                        <Card className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group h-full">
                          <CardContent className="pt-4 pb-4">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="font-medium group-hover:text-primary transition-colors">
                                {doc.title}
                              </h3>
                              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                            </div>
                            {doc.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                {doc.description}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* No content message */}
              {!docContent && childDocs.length === 0 && <DocumentationPlaceholder />}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function DocumentationSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
      <div className="pt-4">
        <Skeleton className="h-6 w-1/2" />
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </div>
    </div>
  );
}

function DocumentationError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <AlertCircle className="h-6 w-6 text-destructive" />
      </div>
      <h3 className="font-semibold mb-2">Unable to Load Documentation</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-xs">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Try Again
      </Button>
    </div>
  );
}

function DocumentationPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <ExternalLink className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="font-semibold mb-2">Documentation Coming Soon</h3>
      <p className="text-sm text-muted-foreground max-w-xs">
        Documentation for this section is being prepared. Check back soon!
      </p>
      <Link to="/docs" className="mt-4">
        <Button variant="outline" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Documentation Hub
        </Button>
      </Link>
    </div>
  );
}
